import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Input, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
interface Customer {
  id: string;
  mobile: number;
  is_active: number;
  amount: string;
  total_package_amount: string;
  currency: string;
  amount_spent: string;
  amount_without_currency: string;
  total_package_amount_without_currency: string;
  amount_spent_without_currency: string;
}

export default function CustomerReport() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Customer[]>([]);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const fetchData = async (page = 1, perPage = 10) => {
    if (filterText.trim() === "") {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}customer_detail/getall`, {
          params: {
            page,
            per_page: perPage,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data: Customer[] = response.data.data;
        setFilteredItems(data);
        setTotalRows(response.data.total);
        setCurrentPage(response.data.current_page);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (filterText.trim() === "") {
      fetchData(currentPage, perPage); // Go back to original page
      setCurrentPage(currentPage); // Sync UI page
    }
  }, [filterText]);
  useEffect(() => {
    if (filterText.trim() === "") {
      fetchData(currentPage, perPage);
    }
  }, [currentPage, perPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage); // pass search even when paginating after search
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage);
  };

  const fetchAllCustomerDataWithoutPagination = async (
    searchText = ""
  ): Promise<Customer[]> => {
    try {
      const response = await axios.get(
        `${baseUrl}customer_detail/getallnopagination`,
        {
          params: {
            search: searchText.trim() !== "" ? searchText : undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.data || [];
    } catch (error) {
      console.error(
        "Error fetching all customer data without pagination:",
        error
      );
      return []; // Return empty array on error
    }
  };

  const handleSearchClick = async () => {
    const data = await fetchAllCustomerDataWithoutPagination(filterText);
    setFilteredItems(data);
    setTotalRows(data.length); // Update based on fetched data length
    setCurrentPage(1); // Reset to page 1 when searching
  };

  const handleReset = () => {
    setFilterText("");
    setCurrentPage(currentPage); // restore to the previous page
    fetchData(currentPage, perPage); // fetch without search
  };

  const customerTableColumns = [
    {
      name: "S.No",
      cell: (row: Customer, index: number) => (
        <>{(currentPage - 1) * perPage + index + 1}</>
      ),
      width: "20%",
    },
    {
      name: "Customer Mobile",
      selector: (row: Customer) => row.mobile || "N/A",
      width: "20%",
    },
    {
      name: "Total Purchase Amount",
      selector: (row: Customer) => `${row.total_package_amount}`,
      width: "20%",
    },
    {
      name: "Amount Spent",
      selector: (row: Customer) => `${row.amount_spent}`,
      width: "20%",
    },
    {
      name: "Current Balance",
      selector: (row: Customer) => `${row.amount}`,
      width: "20%",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  const exportPDF = async () => {
    const allCustomerData = await fetchAllCustomerDataWithoutPagination(
      filterText
    );
    const tableData = allCustomerData.map((row, index) => [
      index + 1,
      row.mobile || "N/A",
      `${row.total_package_amount_without_currency || "0"}`,
      `${row.amount_spent_without_currency || 0}`,
      `${row.amount_without_currency || "0"}`,
    ]);

    exportToPDF({
      fileName: "Customer_Report",
      columns: [
        "S.No",
        "Customer Mobile",
        "Total Purchase Amount",
        "Amount Spent",
        "Current Balance",
      ],
      data: tableData,
      emptyDataMessage: "No customer data available for export.",
    });
  };


const exportExcel = async () => {
  // 1️⃣ Fetch & guard
  const allCustomerData = await fetchAllCustomerDataWithoutPagination(filterText);
  if (!allCustomerData.length) {
    toast.warn("No customer data available for export.", {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
    });
    return;
  }

  // 2️⃣ Build tableData
  const tableData = allCustomerData.map((row, index) => ({
    "S.No": index + 1,
    "Customer Mobile": row.mobile || "N/A",
    "Total Purchase Amount": `${row.total_package_amount}`,
    "Amount Spent": row.amount_spent != null ? `${row.amount_spent}` : "N/A",
    "Current Balance": row.amount != null ? `${row.amount}` : "N/A",
  }));

  // 4️⃣ Export in one call
  excelExportUtils(
    tableData,
    "Customer_Report",  
    "Customers Summary"     
  );
};

  return (
    <>
      <div className="page-body">
        <Breadcrumbs
          pageTitle="Customer Report"
          parent="Customer"
          title="Customer Report"
        />
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              <Row className=" justify-content-between">
                {/* Left side: Excel & PDF buttons */}
                <Col xl={2}>
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={exportPDF}
                      className="d-flex justify-content-center  p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="PDF"
                    >
                      <FaFilePdf size={16} />
                    </Button>
                    <Button
                      color="secondary"
                      onClick={exportExcel}
                      className="d-flex justify-content-center  p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="EXCEL"
                    >
                      <FaFileExcel size={16} />
                    </Button>
                  </div>
                </Col>

                {/* Right side: Search box and buttons */}
                <Col xl={4}>
                  <div className="mb-3 d-flex align-items-center">
                    <Input
                      type="text"
                      placeholder="Search"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      className="me-2"
                    />
                    <Button
                      color="primary"
                      onClick={handleSearchClick}
                      className="me-2"
                      title="Search"
                    >
                      <i className="fa fa-search" />
                    </Button>
                    <Button
                      color="danger"
                      onClick={() => handleReset()}
                      title="Reset Search"
                    >
                      <i className="fa fa-refresh" />
                    </Button>
                  </div>
                </Col>
              </Row>

              <div className="table-responsive custom-scrollbar">
                <DataTable
                  data={filteredItems}
                  columns={customerTableColumns}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                  pagination
                  paginationServer
                  paginationPerPage={perPage}
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  paginationDefaultPage={currentPage}
                  responsive
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <ToastContainer />
      </div>
    </>
  );
}
